/** Where a number came from, made impossible to leave off.
 *
 *  THE BUG THIS EXISTS TO PREVENT, WHICH HAS NOW HAPPENED THREE TIMES. 0207 headlined parcel cover
 *  as though it were canvassability. 0215 found the coverage screen headlining `on_the_building` --
 *  a count of geocode LABELS inherited from TxGIO, which aggregates 48 911 authorities and
 *  publishes no placement-method field -- as though it were the footprint measurement. And the
 *  residential rate shipped on Home reading 97.1 per cent where the measurement is nearer 92,
 *  because it divided that same label by every address rather than by the ones actually tested.
 *
 *  Every one of those was a correct calculation with an unstated provenance and an unstated
 *  denominator. Comments did not stop it; naming conventions did not stop it. So the primitive
 *  takes both as REQUIRED arguments: there is no way to render a rate here without saying what it
 *  is over and where it came from, and a reviewer can see the claim without opening the SQL.
 *
 *  THE THREE WORDS, and they are not interchangeable:
 *
 *    measured  a witness tested this specific thing. footprint_check put the pin against an
 *              Overture polygon; the parcel scan looked for a structure on the lot.
 *    labelled  a source asserts it and we cannot check the assertion. Every rooftop-family
 *              geocode_quality is inherited, and none of them is evidence.
 *    counted   plain arithmetic over rows we hold. No claim beyond "this is how many".
 *
 *  And the fourth state, which is not a value: NOT MEASURED. A denominator of zero is never a
 *  zero per cent -- 0197, 0207 and 0210 each had to remove that from a different screen, where an
 *  unrun job rendered as the worst county in Texas.
 */
export type Provenance = "measured" | "labelled" | "counted";

const TONE: Record<Provenance, { color: string; word: string; hint: string }> = {
  measured: { color: "var(--accent,#3fc079)", word: "measured",
    hint: "A witness tested this: the pin against a building footprint, or the lot against the building scan. Evidence, not a claim." },
  labelled: { color: "var(--warn,#e0a93b)", word: "label",
    hint: "Inherited from the source and not checked by us. TxGIO aggregates 48 911 authorities with different placement policies and publishes no placement-method field, so a rooftop label is not evidence that the pin is on a building." },
  counted: { color: "var(--muted,#8a98ad)", word: "counted",
    hint: "A count of rows we hold. No claim beyond how many there are." },
};

export function Chip({ kind }: { kind: Provenance }) {
  const t = TONE[kind];
  return (
    <span title={t.hint}
          style={{ display: "inline-block", fontSize: 10, letterSpacing: ".04em", textTransform: "uppercase",
                   padding: "1px 6px", borderRadius: 3, cursor: "help",
                   color: t.color, border: `1px solid ${t.color}59` }}>{t.word}</span>
  );
}

const n = (x: number) => x.toLocaleString();

/** A rate, which is a claim about a denominator and is rendered as one.
 *
 *  `of` is the denominator IN WORDS -- "pins tested against Overture footprints", not "checked".
 *  It is what the reader needs and the one thing a column header cannot carry. */
export function Rate({ part, whole, of, kind, unmeasured, big = true }: {
  part: number | null | undefined;
  whole: number | null | undefined;
  of: string;
  kind: Provenance;
  /** What to say when there is no denominator. Never "0%": say what has not happened yet. */
  unmeasured: string;
  big?: boolean;
}) {
  const has = part != null && whole != null && whole > 0;
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: big ? 30 : 22, lineHeight: 1.1,
                    color: has ? TONE[kind].color : "var(--muted,#8a98ad)" }}>
        {has ? `${((100 * (part as number)) / (whole as number)).toFixed(1)}%` : "—"}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {has ? <>{n(part as number)} of {n(whole as number)} {of} <Chip kind={kind} /></>
             : <>{unmeasured}</>}
      </div>
    </div>
  );
}

/** A count. Still carries its provenance, because "4,423,050 doors" is a different claim depending
 *  on whether a scan found the buildings or a county asserted them. */
export function Count({ value, of, kind, unmeasured }: {
  value: number | null | undefined; of: string; kind: Provenance; unmeasured: string;
}) {
  return (
    <div>
      <div style={{ fontWeight: 800, fontSize: 22, lineHeight: 1.1,
                    color: value == null ? "var(--muted,#8a98ad)" : undefined }}>
        {value == null ? "—" : n(value)}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
        {value == null ? unmeasured : <>{of} <Chip kind={kind} /></>}
      </div>
    </div>
  );
}

/** The three words, once, where the numbers are. A vocabulary nobody can read is a vocabulary
 *  nobody follows. */
export function Legend() {
  return (
    <div className="muted" style={{ fontSize: 12, display: "flex", gap: 18, flexWrap: "wrap" }}>
      {(["measured", "labelled", "counted"] as Provenance[]).map((k) => (
        <span key={k}><Chip kind={k} /> {TONE[k].hint.split(".")[0]}.</span>
      ))}
      <span>— means <b>not measured yet</b>, which is never the same as zero.</span>
    </div>
  );
}

/** WHEN a number was taken, made impossible to leave off — the fourth thing a figure has to say.
 *
 *  Provenance above answers "where did this come from" and "over what". This answers the question
 *  none of the screens could answer at all: WHEN. Every headline about the address vault is read
 *  out of address_vault.county_daily, which is a SNAPSHOT, and until 0231 no screen rendered the
 *  date on it. A figure computed an hour ago and one computed three weeks ago were pixel-identical,
 *  so the only way to learn which you were looking at was to re-run the statewide rollup and see
 *  whether the numbers moved. That is how a nightly job became a person's chore.
 *
 *  It is the same defect class as a label headlined as a measurement, one axis over: a memory of a
 *  measurement, presented as the measurement.
 *
 *  TWO STATES, AND THE QUIET ONE IS THE COMMON ONE. Normally this is a date in small grey type and
 *  nothing else — the rollup runs nightly, so "as of today" is the boring truth and a banner would
 *  be noise. It goes amber only when `stale`, which the database decides (a county left behind by a
 *  partial run, a schema change that asked for a recompute nothing has served, or a run genuinely
 *  missed) and explains in `why`. Deliberately NOT stale: pins placed since the snapshot. One
 *  person correcting one pin must not paint every screen in the portal amber until morning — a
 *  banner that is routinely wrong teaches people to stop reading banners. The drift is shown as a
 *  number beside the date instead, so a reader can size it and judge for themselves.
 */
export type Freshness = {
  as_of: string | null; computed_at: string | null; age_hours: number | null;
  counties: number; counties_behind: number; placements_since: number;
  stale: boolean; why: string | null;
};

export function Vintage({ f, className = "" }: { f?: Freshness | null; className?: string }) {
  // Null is not "fresh". An API in front of a database that has not taken 0231 returns nothing
  // here, and the honest render of that is the same silence the screens had before — never a
  // reassuring "as of today" for a date nobody supplied.
  if (!f) return null;
  const when = f.as_of
    ? new Date(f.as_of + "T00:00:00").toLocaleDateString(undefined,
        { month: "short", day: "numeric", year: "numeric" })
    : null;
  const drift = f.placements_since > 0
    ? `${f.placements_since.toLocaleString()} pin${f.placements_since === 1 ? "" : "s"} moved since`
    : null;
  return (
    <div className={className} style={{ fontSize: 11, lineHeight: 1.45, marginTop: 6,
                                        color: f.stale ? "var(--warn,#e0a93b)" : "var(--muted,#8a98ad)" }}>
      <span style={{ fontVariantNumeric: "tabular-nums" }}>
        {when ? `Counted as of ${when}` : "Never counted"}
      </span>
      {drift && <span> · {drift}</span>}
      {f.counties > 0 && <span> · {f.counties.toLocaleString()} counties</span>}
      {/* THE REASON, NOT JUST THE COLOUR. An amber number with no explanation sends the reader to
          ask someone; the database already computed the sentence, so print it. */}
      {f.stale && f.why && (
        <div style={{ marginTop: 3, fontWeight: 600 }}>{f.why} Run “Vault · roll up county state”.</div>
      )}
    </div>
  );
}
